/**
 * Pure helpers for MassagerDetailModal (defaults, display strings, API payload).
 */

import { MASSAGER_GPS_RANGE_OPTIONS } from '@/pages/main/massager/constants';
import type { MassagerClinicLocation, MassagerUpdateRequest } from '@/pages/main/massager/types';
import type { MassagerDetailFormData } from '@/schemas/massagerDetailSchema';
import { formatPeriodDisplay } from '@/utils/dateConverter';

export const MASSAGER_DETAIL_FIELD_LABEL_CLASS =
  'mb-0.5 text-base font-medium uppercase tracking-wide text-gray-400';

export function getMassagerDetailDefaultValues(
  row: MassagerClinicLocation | null
): MassagerDetailFormData {
  if (!row) {
    return {
      email: '',
      login_id: '',
      password: '',
      alertType: null,
      alertDay: '',
      alertMonth: '1',
      gpsRange: null,
    };
  }
  const m = row.massager;
  const numGps = Number(m?.is_gps_range);
  const isConsentAlertOff = m?.is_consent_alert_off;
  const consentVal: 1 | 2 | 3 | null =
    isConsentAlertOff === 1 || isConsentAlertOff === 2 || isConsentAlertOff === 3
      ? isConsentAlertOff
      : null;
  const gpsVal: 1 | 2 | 3 | null = numGps === 1 || numGps === 2 || numGps === 3 ? numGps : null;

  return {
    email: m?.email ?? '',
    login_id: m?.login_id ?? '',
    password: m?.password ?? '',
    alertType: consentVal,
    alertDay: m?.alert_day != null ? String(m.alert_day) : '',
    alertMonth: m?.alert_month != null ? String(m.alert_month) : '1',
    gpsRange: gpsVal,
  };
}

export function formatConsentAlertDisplay(m: MassagerClinicLocation | null): string {
  const v = m?.massager?.is_consent_alert_off;
  if (v === 1) return '通知OFF';
  if (v === 2 && m?.massager?.alert_day != null) return `${m?.massager.alert_day}日前`;
  if (v === 3 && m?.massager?.alert_month != null) return `${m?.massager.alert_month}ヶ月前`;
  return '';
}

export function formatGpsDisplay(m: MassagerClinicLocation | null): string {
  const v = m?.massager?.is_gps_range;
  const opt = MASSAGER_GPS_RANGE_OPTIONS.find(o => o.value === v);
  return opt ? opt.label : '';
}

export function formatApplicablePeriod(m: MassagerClinicLocation | null): string | null {
  if (!m) return null;
  const result = formatPeriodDisplay(m?.available_from, m?.available_to);
  return result || null;
}

export function mapMassagerDetailFormToUpdateRequest(
  formValues: MassagerDetailFormData,
  checkinEnabled: boolean
): MassagerUpdateRequest {
  const payload: MassagerUpdateRequest = {
    email: formValues.email?.trim() ? formValues.email.trim() : null,
  };
  if (!checkinEnabled) return payload;

  payload.login_id =
    formValues.login_id?.trim() ? formValues.login_id.trim() : null;
  payload.password =
    formValues.password?.trim() ? formValues.password.trim() : null;

  if (formValues.alertType != null) {
    payload.is_consent_alert_off = formValues.alertType;
    if (formValues.alertType === 2) {
      payload.alert_day = parseOptionalIntField(formValues.alertDay);
    }
    if (formValues.alertType === 3) {
      payload.alert_month = parseOptionalIntField(formValues.alertMonth);
    }
  }
  if (formValues.gpsRange != null) {
    payload.is_gps_range = formValues.gpsRange;
  }
  return payload;
}

function parseOptionalIntField(s: string | undefined): number | undefined {
  const t = s?.trim();
  return t ? parseInt(t, 10) : undefined;
}

export function getMassagerDetailModalTitle(isEditMode: boolean, isA2: boolean): string {
  if (isEditMode) {
    return isA2 ? 'はり・きゅう師編集' : 'マッサージ師編集';
  }
  return isA2 ? 'はり・きゅう師詳細' : 'マッサージ師詳細';
}

export function getMassagerLoginLabel(isA2: boolean): string {
  return isA2 ? 'はり・きゅう師ログインID' : 'マッサージ師ログインID';
}
